export type WhatsAppWebStage = 'checking' | 'starting' | 'ready' | 'preparing' | 'qr' | 'activating';
export type WhatsAppWebMode = 'prepare' | 'connect';
export type WhatsAppWebIssue = { message: string; action: 'retry' | 'refresh' | 'close' };

type ConnectionOptions = {
  start: () => Promise<any>;
  getStatus: () => Promise<any>;
  // Optional legacy completion hook; the channel selector now owns routing writes.
  activate?: () => Promise<any>;
  onQr: (qr: string) => Promise<void>;
  onStage: (stage: WhatsAppWebStage) => void;
  onIssue: (issue: WhatsAppWebIssue) => void;
  onConnected: () => void;
};

type Status = { connected?: boolean; connectionState?: string; hasQr?: boolean; stopReconnect?: boolean; qr?: string | null };
const stoppedStates = ['stopped', 'auth_failure', 'session_corrupt', 'reconnect_stopped', 'error', 'idle'];
const errorText = (error: any) => `${error?.detalhe || ''} ${error?.error || ''} ${typeof error?.message === 'string' ? error.message : ''}`;
const isMissing = (error: any) => (error?.status === 404 || errorText(error).includes('Status Code: 404'))
  && /Bot .*não encontrado/i.test(errorText(error));
const isAuthError = (error: any) => error?.status === 401;

function classify(status: Status): 'connected' | 'available' | 'stopped' | 'unknown' {
  if (status.stopReconnect === true || stoppedStates.includes(status.connectionState || '')) return 'stopped';
  // Um QR pendente prevalece sobre a flag antiga de conexão.
  if (status.hasQr === true || status.connectionState === 'qr') return 'available';
  if (status.connected === true && status.connectionState === 'open') return 'connected';
  if (status.connected === false || ['starting', 'close', 'reconnecting', 'reconnect_degraded'].includes(status.connectionState || '')) return 'available';
  return 'unknown';
}

// Duas ações: preparar as instruções ou conectar/atualizar o QR.
// Nenhuma delas reinicia, remove ou desconecta uma instância existente.
export function startWhatsAppWebConnection(options: ConnectionOptions, mode: WhatsAppWebMode = 'prepare'): () => void {
  let active = true;
  let stage: WhatsAppWebStage;
  let lastQr: string | undefined;
  let initialized = false;
  let statusUnavailable = false;
  let nextPoll: ReturnType<typeof setTimeout> | undefined;
  let deadline: ReturnType<typeof setTimeout> | undefined;
  const cancel = () => {
    active = false;
    clearTimeout(nextPoll);
    clearTimeout(deadline);
  };
  const issue = (message: string, action: WhatsAppWebIssue['action'] = 'retry') => {
    if (!active) return;
    cancel();
    options.onIssue({ message, action });
  };
  const enterStage = (next: WhatsAppWebStage, renew = false) => {
    if (!active || (stage === next && !renew)) return;
    stage = next;
    options.onStage(next);
    clearTimeout(deadline);
    if (next === 'ready') return;
    deadline = setTimeout(() => {
      if (stage === 'activating') issue('Não foi possível confirmar o canal de envio. Tente novamente.');
      else if (statusUnavailable || stage === 'checking') issue('Não foi possível verificar a conexão agora. Tente novamente.');
      else if (stage === 'qr') issue('O QR Code expirou. Atualize o código para continuar.', 'refresh');
      else if (stage === 'starting') issue('Não foi possível preparar o WhatsApp Web. Tente novamente.');
      else issue('O QR Code ainda não ficou disponível. Tente novamente.');
    }, next === 'qr' ? 120000 : 60000);
  };
  const readStatus = async (): Promise<Status | null> => {
    try {
      const response = await options.getStatus();
      const status = response?.message;
      if (!status || typeof status !== 'object' || classify(status) === 'unknown') throw new Error('Status desconhecido');
      return status;
    } catch (error) {
      // Somente a ausência explícita do bot permite inicializá-lo. Um 404 de rota
      // ou timeout não prova que uma sessão existente está desconectada.
      if (isMissing(error)) return null;
      throw error;
    }
  };
  const finishIfNeeded = async (status: Status) => {
    if (classify(status) === 'connected') {
      if (!options.activate) { cancel(); options.onConnected(); return true; }
      enterStage('activating');
      try {
        const result = await options.activate();
        if (!active) return true;
        if (result?.activeChannel !== 'baileys') throw new Error('Canal não confirmado');
        cancel();
        options.onConnected();
      } catch (error: any) {
        if (!active) return true;
        const controlled = [403, 409].includes(error?.status) && typeof error?.message === 'string';
        issue(controlled ? error.message : isAuthError(error)
          ? 'Sua sessão expirou. Entre novamente para ativar o WhatsApp.'
          : 'O WhatsApp está conectado, mas não foi possível ativar os envios automáticos. Tente novamente.',
        isAuthError(error) || error?.status === 403 ? 'close' : 'retry');
      }
      return true;
    }
    if (classify(status) === 'stopped') {
      issue('O WhatsApp Web está parado e precisa de suporte para voltar a conectar.', 'close');
      return true;
    }
    return false;
  };
  const initializeMissing = async () => {
    if (initialized) throw new Error('Instância ainda indisponível após início');
    initialized = true;
    enterStage('starting');
    try {
      await options.start();
    } catch (error) {
      if (!active) return;
      // Outra aba pode ter iniciado entre o GET e o POST. Reconsulta a instância
      // antes de aceitá-la; "já inicializado" sozinho não comprova saúde.
      if (!errorText(error).includes('já está inicializado')) throw error;
      const status = await readStatus();
      if (!active) return;
      if (!status) throw error;
      await finishIfNeeded(status);
    }
  };

  const poll = async (snapshot?: Status) => {
    if (!active) return;
    try {
      const status = snapshot || await readStatus();
      if (!active) return;
      statusUnavailable = false;
      if (status && await finishIfNeeded(status)) return;
      if (!status) await initializeMissing();
      if (!active) return;
      // Estado e QR vêm do mesmo snapshot, sem segunda chamada por ciclo.
      // Também rejeita códigos antigos associados a outros estados.
      const qr = status?.connectionState === 'qr' ? status.qr : null;
      if (typeof qr === 'string' && qr) {
        const changed = lastQr !== qr;
        enterStage('qr', changed);
        if (changed) {
          lastQr = qr;
          try { await options.onQr(qr); }
          catch { issue('Não foi possível exibir o QR Code. Tente novamente.'); }
        }
      } else {
        lastQr = undefined;
        enterStage('preparing');
      }
    } catch (error) {
      if (!active) return;
      if (isAuthError(error)) {
        issue('Sua sessão expirou. Entre novamente para conectar o WhatsApp.', 'close');
        return;
      }
      statusUnavailable = true;
      // Uma falha de rede durante a leitura não confirma nem desfaz o vínculo.
    }
    if (active) nextPoll = setTimeout(() => { void poll(); }, 3000);
  };

  enterStage('checking');
  const run = async () => {
    try {
      const status = await readStatus();
      if (!active) return;
      if (status && await finishIfNeeded(status)) return;
      if (!status) await initializeMissing();
      if (!active) return;
      if (mode === 'prepare') {
        enterStage('ready');
        cancel(); // Não consome o prazo do QR enquanto o usuário lê as instruções.
      } else {
        enterStage('preparing');
        await poll(status || { connected: false });
      }
    } catch (error) {
      if (isAuthError(error)) issue('Sua sessão expirou. Entre novamente para conectar o WhatsApp.', 'close');
      else issue(stage === 'starting'
        ? 'Não foi possível preparar o WhatsApp Web. Tente novamente.'
        : 'Não foi possível verificar a conexão agora. Tente novamente.');
    }
  };
  void run();
  return cancel;
}
